<script setup lang="ts">
import {
	onMounted,
	ref,
	watch,
	toRefs,
	computed,
	inject,
	provide,
	shallowRef,
	withDefaults,
	ShallowRef,
} from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import SingleMetricResponseDTO = App.Data.Explorer.DataTable.SingleMetricResponseDTO;
import DocumentDO = App.Data.Explorer.DocumentDO;
import SingleMetricDisplay from "@/modules/explorer/components/SingleMetric/SingleMetricDisplay.vue";
import { addWatcherForRealtime } from "@/modules/explorer/composables/realtime-watcher";
import {
	derivedDocumentKey,
	documentKey,
} from "@/modules/explorer/ExplorerUtils";
import { ApiError, Result } from "@/api/base";

const emit = defineEmits([
	"update:isLoading",
	"define-title",
	"update:derivedDocument",
]);

const isTeleportContainerRendered = ref(false);

const isLoading = ref(false);
const noDataForSelection = ref(false);

const explorerStore = useExplorerStore();

const props = withDefaults(
	defineProps<{
		lastUpdated: number;
		hideTitle: boolean;
		fetchDataForSingleMetric: () => Promise<Result<[], ApiError>>;
	}>(),
	{
		hideTitle: false,
	}
);

const { lastUpdated } = toRefs(props);

const injectedDocument = inject(documentKey);

const document = computed(() => {
	return injectedDocument?.value ?? explorerStore.document;
});

const derivedDocument: ShallowRef<DocumentDO> = shallowRef(document.value);

provide(derivedDocumentKey, derivedDocument);

watch(
	derivedDocument,
	() => {
		explorerStore.derivedDocument = derivedDocument.value;
	},
	{ immediate: true }
);

addWatcherForRealtime(() => fetchDataForSingleMetric(true));

onMounted(() => {
	fetchDataForSingleMetric();
	setTimeout(() => {
		isTeleportContainerRendered.value = true;
	}, 100);
});

watch(lastUpdated, async () => {
	await fetchDataForSingleMetric();
});

const singleMetric = ref<SingleMetricResponseDTO | null>(null);

const emitTitleToParent = (value) => {
	emit("define-title", value);
};

const fetchDataForSingleMetric = async (inBackground: boolean = false) => {
	explorerStore.updateLoadingState(true, inBackground, isLoading);

	const response = await props.fetchDataForSingleMetric();

	emit("update:derivedDocument", response.value.derivedDocument);

	if (response.isOk()) {
		singleMetric.value = response.value.singleMetric;
		explorerStore.updateLoadingState(false, inBackground, isLoading);
	} else if (response.isErr()) {
		singleMetric.value = null;
		if (response.error.status === 422) {
			noDataForSelection.value = true;
		}
		explorerStore.updateLoadingState(false, inBackground, isLoading);
	}
};

watch(isLoading, (newValue) => emit("update:isLoading", newValue));
</script>

<template>
	<single-metric-display
		v-if="!noDataForSelection"
		:document="document"
		:is-loading="isLoading"
		error-message="Single metric not available. Check configuration in the data table."
		:single-metric="singleMetric"
		:emit-title="emitTitleToParent"
		:hide-title="hideTitle"
	/>

	<v-empty-state
		v-else
		class="h-100"
		icon="fa-duotone fa-cloud-binary"
		title="No data for this configuration"
		text="No data available for the period selection and/or selected filters"
	/>
</template>

<style lang="scss" scoped></style>
